import { isPreviewableType } from '@doss/common-all'
import FileSaver from 'file-saver'
import { truncate } from 'lodash'
import { MouseEvent, useMemo, useState } from 'react'
import { RxCross2, RxFile, RxOpenInNewWindow } from 'react-icons/rx'
import { tss } from 'tss-react'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { gray, uiColors } from '~/common/styles/colors'
import { Document } from '../../../../common/generated/apollo-operations'
import { Chip } from '../../atoms/chip/Chip'
import { ChipProps } from '../../atoms/chip/types'

interface FileChipProps extends Omit<ChipProps, 'variant' | 'color'> {
  document?: Pick<Document, 'id' | 'name' | 'documentType' | 'storageUrl'>
  fileName: string
  fileUrl?: string
}

/**
 * Renders a chip with a document name and download link
 */
export function FileChip({ document, fileName, fileUrl, ...props }: FileChipProps) {
  const [isHovering, setIsHovering] = useState(false)
  const [isHoveringOnDeleteIcon, setIsHoveringOnDeleteIcon] = useState(false)
  const { isMobile } = useDeviceType()

  const { classes } = useStyles({ isHovering, isHoveringOnDeleteIcon })

  const fileNameDisplay = useMemo(() => {
    const [name, extension] = fileName.split('.')

    return truncate(name, { length: 50, omission: '...' }) + '.' + extension
  }, [fileName])

  const handleClick = async (event: MouseEvent<HTMLDivElement, Event>) => {
    if (!fileUrl) {
      return
    }

    if (document && isMobile) {
      // Navigate to the location of the gcs URL when viewing on mobile.
      // The mobile app's webview has a callback which listens changes in location and can respond accordingly.
      // in the case of a GCS URL, it will download the file and present users with a share dialog to share the file to
      // the application of their choice.
      window.location.href = document.storageUrl
    } else if (document && !isPreviewableType(document.documentType)) {
      const response = await fetch(document.storageUrl)
      const blob = await response.blob()
      FileSaver.saveAs(blob, fileNameDisplay)
    } else {
      event.preventDefault()
      window.open(fileUrl, '_blank')
    }
  }

  return (
    <Chip
      {...props}
      color="gray"
      // Only show the delete icon if an onDelete method is defined
      deleteIcon={
        props.onDelete ? (
          <RxCross2
            className="min-h-4 min-w-4"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              props.onDelete?.(event)
            }}
            onPointerOut={() => setIsHoveringOnDeleteIcon(false)}
            onPointerOver={() => setIsHoveringOnDeleteIcon(true)}
            size="16px"
          />
        ) : undefined
      }
      icon={
        // Only show the hover state when user cannot remove the file by clicking the delete icon
        fileUrl && isHovering && !isHoveringOnDeleteIcon ? (
          <RxOpenInNewWindow className={classes.icon} />
        ) : (
          <RxFile className={classes.icon} />
        )
      }
      isRounded={false}
      label={<div className={classes.fileName}>{fileNameDisplay}</div>}
      onClick={handleClick}
      onPointerOut={() => setIsHovering(false)}
      onPointerOver={() => setIsHovering(true)}
      variant="solid"
    />
  )
}

const useStyles = tss
  .withParams<{ isHovering: boolean; isHoveringOnDeleteIcon: boolean }>()
  .create(({ isHovering, isHoveringOnDeleteIcon }) => {
    return {
      icon: {
        fontSize: '16px',
        color: isHovering && !isHoveringOnDeleteIcon ? gray[9] : uiColors.content.primary,
        minWidth: '16px',
        minHeight: '16px',
      },
      fileName: {
        textDecoration: isHovering && !isHoveringOnDeleteIcon ? 'underline' : 'none',
        // Still allow user to click on the link to route to the document even if the chip is
        // disabled
        pointerEvents: 'auto',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    }
  })
